// Paste-import parser (PLAN.md D-23).
// Rules: one item per line; strip "- ", "* ", "• ", "N."/"N)" prefixes;
// first cell of tab-separated lines; trim; drop empties and
// case-insensitive duplicates. A comma does not split a line.

/**
 * Parse a pasted block into cleaned item text lines.
 */
export function parseImportText(raw: string): string[] {
  return parseImportTextWithStats(raw).items;
}

/** Parse pasted items and count case-insensitive repeats within the paste (D12). */
export function parseImportTextWithStats(raw: string): { items: string[]; duplicates: number } {
  const lines = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const seen = new Set<string>();
  const items: string[] = [];
  let duplicates = 0;

  for (const line of lines) {
    let text = line.trim();
    if (!text) continue;

    // Bullet / numbered prefixes (D-23). Allow optional trailing space so
    // a lone "*" / "-" / "•" line becomes empty and is dropped.
    text = text.replace(/^[-*•]\s*/, "");
    text = text.replace(/^\d+[.)]\s*/, "");
    text = text.trim();
    if (!text) continue;

    // First cell of tab-separated lines (spreadsheet paste). Commas stay in the item.
    if (text.includes("\t")) {
      text = text.split("\t")[0]?.trim() ?? "";
    }
    if (!text) continue;

    const key = text.toLowerCase();
    if (seen.has(key)) {
      duplicates += 1;
      continue;
    }
    seen.add(key);
    items.push(text);
  }

  return { items, duplicates };
}

/** Keep preview lines that are not already on the resource, up to remainingSlots. */
export function selectImportItems(
  preview: string[],
  existingTexts: string[],
  remainingSlots: number
): { toAdd: string[]; skippedDuplicate: number; skippedLimit: number } {
  const seen = new Set(existingTexts.map((t) => t.toLowerCase()));
  const toAdd: string[] = [];
  let skippedDuplicate = 0;
  let skippedLimit = 0;
  for (const text of preview) {
    const key = text.toLowerCase();
    if (seen.has(key)) {
      skippedDuplicate += 1;
      continue;
    }
    if (toAdd.length >= remainingSlots) {
      skippedLimit += 1;
      continue;
    }
    seen.add(key);
    toAdd.push(text);
  }
  return { toAdd, skippedDuplicate, skippedLimit };
}

export function importResultMessage(
  added: number,
  skippedDuplicate: number,
  skippedLimit: number
): string {
  if (added === 0 && skippedDuplicate === 0 && skippedLimit === 0) {
    return "No new items to add.";
  }
  const head = `Added ${added} item${added === 1 ? "" : "s"}`;
  let message = skippedDuplicate
    ? `${head} (${skippedDuplicate} already on this list).`
    : `${head}.`;
  if (skippedLimit) {
    message += ` ${skippedLimit} skipped because the list is full.`;
  }
  return message;
}
